/**
 * The bell — Figma's `TopBar` control, live at last.
 *
 * ===========================================================================
 * THE COUNT IS THE SERVER'S, AND THE SENTENCES ARE OURS
 * ===========================================================================
 * `unread` is rendered exactly as the API sent it (see `api.ts` for why it is not
 * derived from the list). The TEXT of each row is written here, because the
 * notifications table stores no message: it stores an event reference, and a row
 * that rendered `work.assigned` raw would be a database word shown to a person.
 *
 * ===========================================================================
 * AN UNKNOWN VERB IS ECHOED, NOT SWALLOWED
 * ===========================================================================
 * `VERB_TEXT` is a lookup with a fallback that shows the verb itself. A future
 * verb this build has no sentence for is still information — "something happened
 * to CRF 12" beats an empty row, and an empty row is what a `?? ''` would give.
 * The same reasoning as the bulk-warning vocabulary in `BulkBar`.
 *
 * ===========================================================================
 * A DROPDOWN MENU, NOT A POPOVER
 * ===========================================================================
 * `@radix-ui/react-popover` is not a dependency of this app, and adding a package
 * for one control would be a dependency for a div. The dropdown primitive the two
 * pickers already use gives the same focus trap, portal and escape behaviour.
 *
 * ===========================================================================
 * READING IS EXPLICIT
 * ===========================================================================
 * Opening the menu does NOT mark anything read. A bell that clears itself on open
 * loses the one thing the operator came for — which of these is new — and makes
 * "I saw it" indistinguishable from "the pointer passed over it". Each row marks
 * ITSELF read when chosen, and the menu stays open while they work through it.
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchNotifications, markNotificationRead, notificationKeys } from './api';
import type { NotificationDto } from './api';

const VERB_TEXT: Readonly<Record<string, string>> = {
  'work.assigned': 'Work was assigned to you',
  'work.returned': 'Work came back to you after testing',
  'test.returned': 'Work came back to you after testing',
  'test.round.recorded': 'A testing round was recorded',
  'crf.confirmed': 'A CRF was confirmed',
  'quote.sent': 'A quote was sent',
  'quote.confirmed': 'A quote was confirmed',
};

export function notificationText(row: NotificationDto): string {
  if (row.verb === null) {
    // The event row is gone (LEFT JOIN). Real, rare, and said plainly rather
    // than rendered as a blank line the operator cannot interpret.
    return 'A notification whose details are no longer available';
  }
  const sentence = VERB_TEXT[row.verb] ?? row.verb;
  if (row.entity_type === null || row.entity_id === null) return sentence;
  return `${sentence} — ${row.entity_type.replace(/_/g, ' ')} ${row.entity_id}`;
}

export function NotificationBell(): React.JSX.Element {
  const queryClient = useQueryClient();
  const feed = useQuery({
    queryKey: notificationKeys.feed(),
    queryFn: ({ signal }) => fetchNotifications(signal),
    // A minute: fresh enough that an assignment shows up without a reload, cheap
    // enough that it is not a poll. The work-pipeline mutations invalidate this
    // key directly, so the common case does not wait for it at all.
    staleTime: 60_000,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationKeys.feed() });
    },
  });

  const unread = feed.data?.unread ?? 0;
  const rows = feed.data?.data ?? [];

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={
          // The count belongs in the ACCESSIBLE NAME, not only in a coloured dot:
          // a screen-reader user is exactly the person who cannot see the dot.
          unread === 0 ? 'Notifications' : `Notifications (${String(unread)} unread)`
        }
        className="relative inline-flex size-9 items-center justify-center rounded-sm text-fg-secondary hover:bg-subtle hover:text-fg"
      >
        <Bell aria-hidden="true" className="size-4" />
        {unread > 0 ? (
          <span
            aria-hidden="true"
            className="tnum absolute right-1 top-1 min-w-4 rounded-pill bg-danger-fg px-1 text-[10px] font-medium leading-4 text-surface"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-[320px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
        >
          <p className="px-2 pb-1 pt-1.5 text-label font-medium text-fg-muted">Notifications</p>

          {feed.isPending ? (
            <p className="px-2 py-2 text-body13 text-fg-muted">Loading…</p>
          ) : feed.isError ? (
            // NOT an empty list: "could not be read" is not "nothing new", and a
            // silent empty bell after a failed request is a lie of omission.
            <p className="px-2 py-2 text-body13 text-fg-muted">
              Your notifications could not be read just now.
            </p>
          ) : rows.length === 0 ? (
            <p className="px-2 py-2 text-body13 text-fg-muted">Nothing yet.</p>
          ) : (
            <div className="max-h-[320px] overflow-y-auto">
              {rows.map((row) => (
                <DropdownMenu.Item
                  key={row.id}
                  disabled={markRead.isPending}
                  onSelect={(event) => {
                    // The menu stays OPEN: an operator with four unread wants to
                    // read four, not reopen the bell three times.
                    event.preventDefault();
                    if (row.read_at === null) markRead.mutate(row.id);
                  }}
                  className={`flex w-full cursor-default select-none flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-left outline-none data-[highlighted]:bg-subtle ${
                    row.read_at === null ? 'font-medium text-fg' : 'text-fg-muted'
                  }`}
                >
                  <span className="text-body13">{notificationText(row)}</span>
                  <span className="tnum text-label text-fg-muted">
                    {new Date(row.created_at).toLocaleString()}
                    {row.read_at === null ? ' · unread' : ''}
                  </span>
                </DropdownMenu.Item>
              ))}
            </div>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
